import styled from 'styled-components';
import {
  PRIMARY_COLOR,
  PRIMARY_TEXT_COLOR,
  SECONDARY_TEXT_COLOR,
  NICE_BG_FOR_CARDS,
  NICE_BG_FOR_CARDS_HOVER,
  GOLD_COLOR,
  PRIMARY_COLOR_DIMMED,
} from '../../constants';

// Container for the entire leaderboard
export const LeaderboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  padding-bottom: 80px;
`;

// Header styles
export const LeaderboardHeader = styled.div`
  text-align: center;
  margin-bottom: 20px;

  h1 {
    font-size: 2rem;
    margin-bottom: 8px;
    color: ${PRIMARY_TEXT_COLOR};
  }

  p {
    color: ${SECONDARY_TEXT_COLOR};
  }
`;

// Tab container styles
export const TabsContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  overflow-x: auto;
  padding-bottom: 10px;

  /* Custom scrollbar for the tabs */
  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${PRIMARY_COLOR_DIMMED};
    border-radius: 2px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 2px;
  }

  /* Mobile-specific styling */
  @media (max-width: 576px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    overflow-x: visible;
  }
`;

// Tab button styles
interface TabProps {
  active: boolean;
}

export const Tab = styled.button<TabProps>`
  padding: 10px 16px;
  background-color: ${(props) =>
    props.active ? PRIMARY_COLOR : 'rgba(255, 255, 255, 0.05)'};
  color: ${PRIMARY_TEXT_COLOR};
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-weight: ${(props) => (props.active ? 'bold' : 'normal')};
  transition: all 0.3s ease;

  &:hover {
    background-color: ${(props) =>
      props.active ? PRIMARY_COLOR : 'rgba(255, 255, 255, 0.08)'};
    transform: translateY(-2px);
  }

  /* Mobile-specific styling */
  @media (max-width: 576px) {
    width: 100%;
    padding: 12px 8px;
    font-size: 0.9rem;
    white-space: nowrap;
    display: flex;
    justify-content: center;
    align-items: center;
    transform: none;
    overflow: hidden; /* Prevent overflow effects */
    
    &:hover {
      transform: none;
      background-color: ${(props) =>
        props.active ? PRIMARY_COLOR : 'rgba(255, 255, 255, 0.05)'}; /* Reset hover background */
    }
  }
`;

// Leaderboard list container
export const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// Entry container
export const EntryContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 15px;
  background-color: ${NICE_BG_FOR_CARDS};
  border-radius: 10px;
  gap: 15px;
  transition: transform 0.2s ease, background-color 0.2s ease;
  cursor: pointer;

  &:hover {
    background-color: ${NICE_BG_FOR_CARDS_HOVER};
    transform: translateY(-2px);
  }

  &:nth-child(1) {
    background: linear-gradient(
      90deg,
      ${NICE_BG_FOR_CARDS} 0%,
      rgba(255, 215, 0, 0.15) 100%
    );
    border-left: 3px solid ${GOLD_COLOR};
  }

  &:nth-child(2) {
    background: linear-gradient(
      90deg,
      ${NICE_BG_FOR_CARDS} 0%,
      rgba(192, 192, 192, 0.15) 100%
    );
    border-left: 3px solid silver;
  }

  &:nth-child(3) {
    background: linear-gradient(
      90deg,
      ${NICE_BG_FOR_CARDS} 0%,
      rgba(205, 127, 50, 0.15) 100%
    );
    border-left: 3px solid #cd7f32;
  }
`;

// Rank number
export const RankNumber = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  width: 40px;
  text-align: center;
  color: ${PRIMARY_TEXT_COLOR};
`;

// User info container
export const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

// User name
export const UserName = styled.div`
  font-weight: bold;
  font-size: 1.1rem;
  color: ${PRIMARY_TEXT_COLOR};
`;

// User score
export const UserScore = styled.div`
  color: ${SECONDARY_TEXT_COLOR};
  display: flex;
  align-items: center;

  &::before {
    margin-right: 5px;
    font-size: 0.9rem;
  }
`;

// Loading message
export const LoadingMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: ${SECONDARY_TEXT_COLOR};
`;

// Error message
export const ErrorMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #ff6b6b;
`;
